/**
 * Map Pre-warming and Pre-fetching Service
 * Preloads Leaflet JS/CSS and proactively caches map tiles for selected itineraries.
 */

let leafletPreloadPromise = null;

/**
 * Preloads Leaflet and MarkerCluster dependencies in the background.
 */
export function preloadMapLibrary() {
    if (leafletPreloadPromise) return leafletPreloadPromise;

    leafletPreloadPromise = (async () => {
        try {
            // Preload Leaflet and styles concurrently
            const [leafletModule] = await Promise.all([
                import('leaflet'),
                import('leaflet/dist/leaflet.css'),
                import('leaflet.markercluster'),
                import('leaflet.markercluster/dist/MarkerCluster.css'),
                import('leaflet.markercluster/dist/MarkerCluster.Default.css'),
                import('./audio.js')
            ]);
            return leafletModule.default;
        } catch (err) {
            console.warn('[MapPrefetch] Failed to preload map libraries in background:', err);
            return null;
        }
    })();

    return leafletPreloadPromise;
}

/**
 * Convert Latitude, Longitude and Zoom to Tile Coordinates (Slippy Map standard)
 */
function latLngToTile(lat, lng, zoom) {
    const latRad = (lat * Math.PI) / 180;
    const n = 2 ** zoom;
    const x = Math.floor(((lng + 180) / 360) * n);
    const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
    return { x: Math.max(0, Math.min(x, n - 1)), y: Math.max(0, Math.min(y, n - 1)), z: zoom };
}

/**
 * Generates CartoDB Voyager tile URLs for a specific bounding box and zoom levels.
 */
function getTileUrlsForBounds(minLat, maxLat, minLng, maxLng, zooms = [14, 15, 16]) {
    const urls = new Set();
    const subdomains = ['a', 'b', 'c', 'd'];

    zooms.forEach(z => {
        const p1 = latLngToTile(maxLat, minLng, z);
        const p2 = latLngToTile(minLat, maxLng, z);

        const minX = Math.min(p1.x, p2.x);
        const maxX = Math.max(p1.x, p2.x);
        const minY = Math.min(p1.y, p2.y);
        const maxY = Math.max(p1.y, p2.y);

        // Safeguard to avoid over-fetching if bounding box is huge
        if ((maxX - minX + 1) * (maxY - minY + 1) > 40) {
            // For wide bounds, just take center tiles and corners
            const midX = Math.floor((minX + maxX) / 2);
            const midY = Math.floor((minY + maxY) / 2);
            const keyPoints = [
                { x: midX, y: midY },
                { x: minX, y: minY },
                { x: maxX, y: maxY }
            ];
            keyPoints.forEach(({ x, y }) => {
                const s = subdomains[(x + y) % subdomains.length];
                urls.add(`https://${s}.basemaps.cartocdn.com/rastertiles/voyager/${z}/${x}/${y}.png`);
            });
            return;
        }

        for (let x = minX; x <= maxX; x++) {
            for (let y = minY; y <= maxY; y++) {
                const s = subdomains[(x + y) % subdomains.length];
                urls.add(`https://${s}.basemaps.cartocdn.com/rastertiles/voyager/${z}/${x}/${y}.png`);
            }
        }
    });

    return Array.from(urls);
}

/**
 * Prewarms map tiles for an itinerary into the CacheStorage / Service Worker.
 */
export async function prewarmRouteTiles(levels) {
    if (!levels || !levels.length) return;

    // Filter valid coordinates
    const validCoords = levels.filter(l => l.coords && l.coords.lat && l.coords.lng).map(l => l.coords);
    if (!validCoords.length) return;

    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    validCoords.forEach(c => {
        if (c.lat < minLat) minLat = c.lat;
        if (c.lat > maxLat) maxLat = c.lat;
        if (c.lng < minLng) minLng = c.lng;
        if (c.lng > maxLng) maxLng = c.lng;
    });

    // Expand margin slightly (approx 500m)
    const margin = 0.008;
    minLat -= margin;
    maxLat += margin;
    minLng -= margin;
    maxLng += margin;

    const tileUrls = getTileUrlsForBounds(minLat, maxLat, minLng, maxLng, [14, 15, 16]);

    // Pre-fetch tiles with concurrency limit
    const queue = [...tileUrls];
    const maxConcurrent = 4;
    let running = 0;

    async function fetchNext() {
        if (!queue.length) return;
        const url = queue.shift();
        running++;
        try {
            // Using fetch with cache first strategy
            await fetch(url, { mode: 'cors', cache: 'force-cache' }).catch(() => {});
        } catch (_) {
            // Ignore offline or silent errors
        } finally {
            running--;
            if (queue.length) await fetchNext();
        }
    }

    const workers = Array.from({ length: Math.min(maxConcurrent, queue.length) }, () => fetchNext());
    await Promise.all(workers);
}

/**
 * Automatically schedule idle preloading
 */
export function scheduleIdleMapPreload() {
    if (typeof window === 'undefined') return;

    const run = () => {
        preloadMapLibrary();
    };

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 3000 });
    } else {
        setTimeout(run, 1500);
    }
}
